import React, { useState, useEffect } from 'react';
import { mdiInformationOutline } from '@mdi/js';
import { Icon } from '@mdi/react';
import { CollapsibleContainer } from '../../base/collapsible-container';
import { CodingDetailModal } from './coding-detail-modal';
import { ICodingsTableProps } from './coding.types';
import { CoreFetch } from '../../../api';
import { CodingApi } from '../../../api/server';
import { ProviderCoding, SupportCoding } from '../../../api/server/models';
import * as S from './chat-body.styles';

interface ICodingWithNotes {
    id: string;
    notes?: string;
}

export const CodingsTable: React.FunctionComponent<ICodingsTableProps> = (
    props: ICodingsTableProps
): React.ReactElement => {
    const { codingIds, isSupportApp } = props;
    const [codings, setCodings] = useState<ICodingWithNotes[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedCodingId, setSelectedCodingId] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        if (codingIds && codingIds.length > 0) {
            fetchCodingsNotes();
        }
    }, [codingIds, isSupportApp]);

    const fetchCodingsNotes = async () => {
        setLoading(true);
        try {
            const codingApi = CoreFetch(CodingApi);
            const codingPromises = codingIds.map(async (id) => {
                try {
                    let coding: ProviderCoding | SupportCoding;
                    if (isSupportApp) {
                        coding = await codingApi.getSupportCoding({ id });
                    } else {
                        coding = await codingApi.getProviderCoding({ id });
                    }
                    return {
                        id: coding.id || id,
                        notes: coding.notes
                    };
                } catch (err) {
                    console.error(`Failed to fetch coding ${id}:`, err);
                    return {
                        id,
                        notes: 'Error loading notes'
                    };
                }
            });

            const codingsData = await Promise.all(codingPromises);
            setCodings(codingsData);
        } catch (err) {
            console.error('Failed to fetch codings:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleInfoClick = (codingId: string) => {
        setSelectedCodingId(codingId);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedCodingId(null);
    };

    const formatNotes = (notes?: string): string => {
        if (!notes) return 'No notes';
        
        // Handle double encoded notes
        try {
            const parsed = JSON.parse(notes);
            if (typeof parsed === 'string') {
                return parsed;
            }
            return JSON.stringify(parsed, null, 2);
        } catch (e) {
            return notes;
        }
    };

    const renderTableContent = () => {
        if (loading) {
            return (
                <S.EmptyMessage>Loading codings...</S.EmptyMessage>
            );
        }

        if (!codingIds || codingIds.length === 0) {
            return (
                <S.EmptyMessage>No coding records</S.EmptyMessage>
            );
        }

        return (
            <S.CodingsTableContainer>
                <S.CodingsTable>
                    <S.CodingsTableHead>
                        <S.CodingsTableRow>
                            <S.CodingsTableHeaderCell>Notes</S.CodingsTableHeaderCell>
                            <S.CodingsTableHeaderCell style={{ width: '100px' }}>Actions</S.CodingsTableHeaderCell>
                        </S.CodingsTableRow>
                    </S.CodingsTableHead>
                    <S.CodingsTableBody>
                        {codings.map((coding) => (
                            <S.CodingsTableRow key={coding.id}>
                                <S.NotesCell title={formatNotes(coding.notes)}>
                                    {formatNotes(coding.notes)}
                                </S.NotesCell>
                                <S.CodingsTableCell>
                                    <S.InfoButton
                                        onClick={() => handleInfoClick(coding.id)}
                                        type="button"
                                        aria-label="View coding details"
                                    >
                                        <Icon path={mdiInformationOutline} size={0.7} />
                                        <span style={{ marginLeft: '4px' }}>Info</span>
                                    </S.InfoButton>
                                </S.CodingsTableCell>
                            </S.CodingsTableRow>
                        ))}
                    </S.CodingsTableBody>
                </S.CodingsTable>
            </S.CodingsTableContainer>
        );
    };

    return (
        <>
            <S.CodingsTableWrapper>
                <CollapsibleContainer title="Codings" defaultOpen={false}>
                    {renderTableContent()}
                </CollapsibleContainer>
            </S.CodingsTableWrapper>
            {selectedCodingId && (
                <CodingDetailModal
                    codingId={selectedCodingId}
                    isSupportApp={isSupportApp}
                    open={modalOpen}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
};

